import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const ORG_SIZES = ["1–10", "11–50", "51–200", "201–1,000", "1,000+"];
const TEAM_SIZES = ["No dedicated team", "1–2", "3–5", "6–15", "16+"];
const SECTORS = [
  "Technology / SaaS",
  "Professional Services",
  "Financial Services",
  "Manufacturing / Industrial",
  "Retail / E-commerce",
  "Healthcare",
  "Education",
  "Non-profit",
  "Other",
];

export default function AboutYou() {
  const navigate = useNavigate();
  const { session, update } = useDiagnostic();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    job_title: "",
    organisation: "",
    organisation_size: "",
    marketing_team_size: "",
    sector: "",
  });

  if (!session.pathway) {
    navigate("/pathway");
    return null;
  }

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const [respondent, questions] = await Promise.all([
        api.createRespondent({ pathway: session.pathway, about: form }),
        api.questions(session.pathway),
      ]);
      update({
        about: form,
        respondentId: respondent.id,
        questions: questions.questions,
        answers: {},
        current: 0,
      });
      navigate("/diagnostic");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const field = "mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]";

  return (
    <div className="min-h-screen bg-white">
      <TopBar step="Step 2 of 4" />
      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="tw-fade-up">
          <p className="tw-eyebrow">About you</p>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            A little context.
          </h1>
          <p className="mt-4 text-lg text-neutral-600">
            This helps us frame your results. We don&apos;t need your email yet.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-12 space-y-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                placeholder="Jane Doe"
                className={field}
                data-testid="about-you-name-input"
              />
            </div>
            <div>
              <Label htmlFor="job">Job title</Label>
              <Input
                id="job"
                value={form.job_title}
                onChange={(e) => set("job_title")(e.target.value)}
                placeholder="Marketing Director"
                className={field}
                data-testid="about-you-job-title-input"
              />
            </div>
            <div>
              <Label htmlFor="org">Organisation</Label>
              <Input
                id="org"
                value={form.organisation}
                onChange={(e) => set("organisation")(e.target.value)}
                placeholder="Acme Inc."
                className={field}
                data-testid="about-you-org-name-input"
              />
            </div>

            <div>
              <Label>Organisation size</Label>
              <Select value={form.organisation_size} onValueChange={set("organisation_size")}>
                <SelectTrigger className={field} data-testid="about-you-org-size-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {ORG_SIZES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Marketing team size</Label>
              <Select value={form.marketing_team_size} onValueChange={set("marketing_team_size")}>
                <SelectTrigger className={field} data-testid="about-you-team-size-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label>Sector</Label>
              <Select value={form.sector} onValueChange={set("sector")}>
                <SelectTrigger className={field} data-testid="about-you-sector-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-neutral-200 pt-8">
            <button
              type="button"
              onClick={() => navigate("/pathway")}
              className="tw-focus inline-flex items-center gap-2 rounded-sm px-4 py-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 hover:text-neutral-900"
              data-testid="about-you-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="tw-btn-primary tw-focus inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide disabled:opacity-40"
              data-testid="about-you-submit-button"
            >
              {submitting ? "Preparing…" : "Begin diagnostic"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
